import type { RescuedLegacyShrinkOperation } from "~/store/shrinkMobileSlice";

import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { type RecordShrinkEventDataInput, type RemoveShrinkEventDataInput } from "~/gql/graphqlBase.generated";
import {
	type RecordShrinkEventMutation,
	type RemoveShrinkEventMutation
} from "~/gql/mutations/produce/productionSchedule.generated";
import logger from "~/helpers/logger";
import awaitQueueEnqueue from "~/helpers/offline/awaitQueueEnqueue";
import classifyMutationFireOutcome, {
	isQueuedNetworkFailure,
	type MutationFireOutcome
} from "~/helpers/offline/classifyMutationFireOutcome";
import applyIdempotencyKey from "~/helpers/offline/idempotencyKey";
import { OFFLINE_QUEUE_APP } from "~/helpers/offline/queueMeta";
import { useAppStore } from "~/hooks/reduxHooks";
import useMutationRunner from "~/hooks/useMutationRunner";
import { useRecordShrinkEventMutation, useRemoveShrinkEventMutation } from "~/services/endpoints/apiEndpoints";

export type RefireLegacyShrinkOperation = (
	operation: RescuedLegacyShrinkOperation
) => Promise<MutationFireOutcome<unknown>>;

export interface UseShrinkMutationsReturn {
	recordShrinkEvent: (
		input: RecordShrinkEventDataInput
	) => Promise<MutationFireOutcome<RecordShrinkEventMutation["recordShrinkEvent"]>>;
	refireRescuedShrinkOperation: RefireLegacyShrinkOperation;
	removeShrinkEvent: (
		input: RemoveShrinkEventDataInput
	) => Promise<MutationFireOutcome<RemoveShrinkEventMutation["removeShrinkEvent"]>>;
}

const useShrinkMutations = (): UseShrinkMutationsReturn => {
	const { t } = useTranslation("mobile");
	const store = useAppStore();
	const { runForResult } = useMutationRunner();
	const [recordShrinkEventMutation] = useRecordShrinkEventMutation();
	const [removeShrinkEventMutation] = useRemoveShrinkEventMutation();

	const fallbackMessage = t("shrink.errors.operationFailed");

	const recordShrinkEvent = useCallback(
		async (
			input: RecordShrinkEventDataInput
		): Promise<MutationFireOutcome<RecordShrinkEventMutation["recordShrinkEvent"]>> => {
			let guarded: RecordShrinkEventDataInput;
			try {
				guarded = applyIdempotencyKey({
					actionKey: input.input.shrinkEventId,
					input,
					mutationName: "recordShrinkEvent"
				});
			} catch (error) {
				logger.error(fallbackMessage, error);
				return { errorMessage: fallbackMessage, outcome: "failed", sessionRenewed: false };
			}
			const result = await runForResult(
				recordShrinkEventMutation({ input: guarded }),
				(data) => data.recordShrinkEvent,
				fallbackMessage,
				{ expectedError: isQueuedNetworkFailure, silent: true }
			);
			const outcome = classifyMutationFireOutcome(result, result.data?.recordShrinkEvent, fallbackMessage);
			if (outcome.outcome !== "queued") return outcome;
			if (await awaitQueueEnqueue(store, OFFLINE_QUEUE_APP.SHRINK, "RecordShrinkEvent", input.input.shrinkEventId))
				return outcome;
			const errorMessage = t("shrink.errors.saveEvent");
			logger.error(errorMessage, result.transportError);
			return { errorMessage, outcome: "failed", sessionRenewed: false };
		},
		[fallbackMessage, recordShrinkEventMutation, runForResult, store, t]
	);

	const removeShrinkEvent = useCallback(
		async (
			input: RemoveShrinkEventDataInput
		): Promise<MutationFireOutcome<RemoveShrinkEventMutation["removeShrinkEvent"]>> => {
			let guarded: RemoveShrinkEventDataInput;
			try {
				guarded = applyIdempotencyKey({
					actionKey: input.shrinkEventId,
					input,
					mutationName: "removeShrinkEvent"
				});
			} catch (error) {
				logger.error(fallbackMessage, error);
				return { errorMessage: fallbackMessage, outcome: "failed", sessionRenewed: false };
			}
			const result = await runForResult(
				removeShrinkEventMutation({ input: guarded }),
				(data) => data.removeShrinkEvent,
				fallbackMessage,
				{ expectedError: isQueuedNetworkFailure, silent: true }
			);
			const outcome = classifyMutationFireOutcome(result, result.data?.removeShrinkEvent, fallbackMessage);
			if (outcome.outcome !== "queued") return outcome;
			if (await awaitQueueEnqueue(store, OFFLINE_QUEUE_APP.SHRINK, "RemoveShrinkEvent", input.shrinkEventId))
				return outcome;
			const errorMessage = t("shrink.errors.deleteEvent");
			logger.error(errorMessage, result.transportError);
			return { errorMessage, outcome: "failed", sessionRenewed: false };
		},
		[fallbackMessage, removeShrinkEventMutation, runForResult, store, t]
	);

	const refireRescuedShrinkOperation = useCallback<RefireLegacyShrinkOperation>(
		(operation) =>
			operation.type === "record" ? recordShrinkEvent(operation.input) : removeShrinkEvent(operation.input),
		[recordShrinkEvent, removeShrinkEvent]
	);

	return { recordShrinkEvent, refireRescuedShrinkOperation, removeShrinkEvent };
};

export default useShrinkMutations;
